import {
  Form,
  Space,
  Switch,
  Input,
  Spin,
  Button,
  Divider,
  Row,
  Col,
  Select,
  Tag,
  InputNumber,
  message,
  DatePicker,
  Tooltip,
  Popconfirm,
} from 'antd';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { useDaumPostcodePopup } from 'react-daum-postcode';

import { useUserInfo } from '../../../hooks';
import { ContentsLayout } from '../../../components/layout';
import { useGetEquipmentDetail, usePatchEquipment, useDeleteEquipment, useGetUsers } from '../../../apis';
import { integerFormatter } from '../../../utils/formatter';

import TabsTable from './tabs';
import DDay from '../util';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

const EquipmentDetail = () => {
  const { rid } = useParams();
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const [isImgLoading, setIsImgLoading] = useState(false);
  const [isChangeLastCheckDate, setIsChangeLastCheckDate] = useState(false);

  const openPostCodePopup = useDaumPostcodePopup();
  const { userInfo, isCustomer } = useUserInfo();

  const { data: equipmentData } = useGetEquipmentDetail({ rid });
  const { data: usersData } = useGetUsers();
  const { mutate: deleteEquipment, isLoading: isDeleteLoading } = useDeleteEquipment();
  const { mutate: patchEquipment, isLoading: isPatchLoading } = usePatchEquipment();

  const handleGetAddress = () => {
    openPostCodePopup({
      onComplete: (addressObj) => {
        form.setFieldValue(`installAddress`, addressObj.address);
      },
    });
  };

  const setInitialValues = () => {
    if (equipmentData)
      form.setFieldsValue({
        name: equipmentData.customer?.name,
        serialNo: equipmentData.serialNo,
        manager: equipmentData.manager,
        useTime: 2000,
        successChargeCount: equipmentData.successChargeCount,
        status: 'READY',
        code: 'PIP2024',
        no: equipmentData.no,
        boosterMethod: equipmentData.boosterMethod,
        boosterTime: equipmentData.boosterTime,
        boosterType: equipmentData.boosterType,
        installAddress: equipmentData.installAddress,
        installAddressDetail: equipmentData.installAddressDetail,
        installedDate: dayjs(equipmentData.installedDate),
        latitude: equipmentData.latitude,
        longitude: equipmentData.longitude,
        manufacturedDate: dayjs(equipmentData.manufacturedDate),
        memo: equipmentData.memo,
        checkDday: Number(equipmentData.checkDday),
        model: equipmentData.model,
        majorSoftwareVersion: Number(equipmentData.softwareVersion.split('.')[0]),
        minorSoftwareVersion: Number(equipmentData.softwareVersion.split('.')[1]),
        patchSoftwareVersion: Number(equipmentData.softwareVersion.split('.')[2]),
      });
  };

  const onPatchEquipment = (values) => {
    patchEquipment(
      {
        rid,
        customerRid: equipmentData?.customerRid,
        boosterMethod: values.boosterMethod,
        serialNo: values.serialNo,
        model: values.model,
        softwareVersion: `${values.majorSoftwareVersion}.${values.minorSoftwareVersion}.${values.patchSoftwareVersion}`,
        latitude: values.latitude,
        longitude: values.longitude,
        boosterTime: values.boosterTime,
        boosterType: values.boosterType,
        lastCheckDate: isChangeLastCheckDate ? dayjs().format('YYYY-MM-DD') : undefined,
        manufacturedDate: dayjs(values.manufacturedDate).format('YYYYMMDD'),
        installedDate: dayjs(values.installedDate).format('YYYYMMDD'),
        installAddress: values.installAddress,
        manager: values.manager,
        no: values.no,
        successChargeCount: values.successChargeCount,
        checkDday: values.checkDday,
        installAddressDetail: values.installAddressDetail,
        memo: values.memo,
      },
      {
        onSuccess: (res) => {
          if (!res.success) return message.error('변경에 실패했습니다.');
          return message.success('변경이 완료되었습니다.');
        },
      },
    );
  };

  const dday = DDay(equipmentData?.lastCheckDate, equipmentData?.checkDday);
  const isPositiveNumber = Math.sign(dday) === 1;

  useEffect(() => {
    setInitialValues();
  }, [equipmentData]);

  return (
    <>
      <ContentsLayout headTitle={`장비상세`}>
        <Form form={form} layout="vertical" className={cx('form')} onFinish={onPatchEquipment}>
          <Row align={'top'} justify={'center'} gutter={[24, 24]} wrap={false}>
            <Col flex={'480px'}>
              <div className={cx('dataImgBox')}>
                <Spin spinning={isImgLoading}>
                  <img
                    src="data:image/jpeg;base64,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"
                    alt="용기 이미지"
                    draggable={false}
                    className={cx('dataImg')}
                  />
                </Spin>

                <Button
                  type="primary"
                  ghost
                  onClick={() => {
                    setIsImgLoading(true);

                    setTimeout(() => {
                      setIsImgLoading(false);
                    }, 1000);
                  }}>
                  Refresh
                </Button>
              </div>
            </Col>
            <Col flex={'auto'}>
              <Space direction="vertical">
                <Space>
                  <Form.Item label="업체명" name="name">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="일련번호" name="serialNo">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item name="model" label="모델명">
                    <Input disabled={isCustomer} />
                  </Form.Item>
                </Space>
                <Space>
                  <Form.Item name="no" label="호기">
                    <InputNumber disabled={isCustomer} />
                  </Form.Item>
                  <Form.Item label="담당자" name="manager">
                    <Select
                      disabled={userInfo?.role === 'SUB-CUSTOMER'}
                      style={{ width: 180 }}
                      options={usersData
                        ?.filter((user) => user.customer?.name === equipmentData?.customer?.name)
                        .map((data) => ({ label: data.name, value: data.name }))}
                    />
                  </Form.Item>
                  <Form.Item label="사용누적시간" name="useTime">
                    <InputNumber disabled={isCustomer} />
                  </Form.Item>
                  <Form.Item label="충전 완료회수" name="successChargeCount">
                    <InputNumber disabled={isCustomer} />
                  </Form.Item>
                </Space>
                <Space>
                  <Form.Item label="실시간 상태" name="status">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="발생 알람코드" name="code">
                    <Input disabled />
                  </Form.Item>
                </Space>
                <Space>
                  <Space>
                    <Form.Item
                      label={
                        <Space>
                          점검카운트(day)
                          <Tag color={isPositiveNumber ? 'green' : 'red'}>
                            Dday : {DDay(equipmentData?.lastCheckDate, equipmentData?.checkDday)}
                          </Tag>
                        </Space>
                      }
                      name="checkDday">
                      <InputNumber
                        disabled={isCustomer}
                        style={{ width: '100%' }}
                        min={1}
                        formatter={integerFormatter}
                      />
                    </Form.Item>

                    <Form.Item label="점검 초기화">
                      <Tooltip title="수정 후 오늘일자로 적용됩니다.">
                        <Switch disabled={isCustomer} onChange={(value) => setIsChangeLastCheckDate(value)} />
                      </Tooltip>
                    </Form.Item>
                  </Space>
                </Space>
                <Space>
                  <Form.Item name="boosterType" label="부스터 방식">
                    <Select
                      disabled={isCustomer}
                      options={[
                        { value: 'Maximator', label: 'Maximator(DLE-30-75)' },
                        { value: 'HII', label: 'HII(HIHPG4-21397)' },
                        { value: 'Haskel', label: 'Haskel(AGT-32/62)' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name="boosterTime" label="부스터 사용 최대시간">
                    <InputNumber min={0} style={{ width: '100%' }} disabled={isCustomer} />
                  </Form.Item>
                  <Form.Item name="boosterMethod" label="부스터 종류">
                    <Select
                      disabled={isCustomer}
                      style={{ minWidth: 180 }}
                      options={[
                        { label: '에어', value: 'AIR' },
                        { label: '전기/단상 110V', value: 'EV110' },
                        { label: '전기/단상 220V', value: 'EV220' },
                      ]}
                    />
                  </Form.Item>
                </Space>
                <Space>
                  <Form.Item name="installedDate" label="설치일자">
                    <DatePicker disabled={isCustomer} />
                  </Form.Item>
                  <Form.Item name="installAddress" label="설치주소">
                    <Input.Search
                      disabled={isCustomer}
                      style={{ minWidth: 280 }}
                      onClick={handleGetAddress}
                      onSearch={handleGetAddress}
                    />
                  </Form.Item>
                  <Form.Item name="installAddressDetail" label="설치주소 상세">
                    <Input disabled={isCustomer} />
                  </Form.Item>
                </Space>
                <Space>
                  <Form.Item name="manufacturedDate" label="제조일자">
                    <DatePicker disabled={isCustomer} />
                  </Form.Item>

                  <Space.Compact>
                    <Form.Item name="majorSoftwareVersion" label="Major" rules={[{ required: true, message: '' }]}>
                      <InputNumber min={0} max={99} disabled={isCustomer} />
                    </Form.Item>
                    <Form.Item name="minorSoftwareVersion" label="Minor" rules={[{ required: true, message: '' }]}>
                      <InputNumber min={0} max={99} disabled={isCustomer} />
                    </Form.Item>
                    <Form.Item name="patchSoftwareVersion" label="Patch" rules={[{ required: true, message: '' }]}>
                      <InputNumber min={0} max={99} disabled={isCustomer} />
                    </Form.Item>
                  </Space.Compact>
                  <Space.Compact align="start">
                    <Form.Item name="latitude" label="위도" rules={[{ required: true, message: '' }]}>
                      <InputNumber disabled={isCustomer} />
                    </Form.Item>
                    <Form.Item name="longitude" label="경도" rules={[{ required: true, message: '' }]}>
                      <InputNumber disabled={isCustomer} />
                    </Form.Item>
                  </Space.Compact>
                </Space>

                <Form.Item name="memo" label="메모">
                  <Input.TextArea disabled={isCustomer} style={{ minWidth: 300, minHeight: 150 }} />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Divider />

          <TabsTable />

          <Row align={'middle'} justify={'space-between'} className={cx('fix')}>
            <Button type="default" onClick={() => navigate(-1)}>
              뒤로
            </Button>
            <Space>
              <Popconfirm
                title="정말 삭제하시겠습니까?"
                cancelText="아니오"
                okText="네"
                onConfirm={() =>
                  deleteEquipment(
                    {
                      rid,
                    },
                    {
                      onSuccess: (res) => {
                        if (!res.success) message.error('삭제에 실패했습니다.');
                        message.success('삭제되었습니다.');
                        navigate(-1);
                      },
                    },
                  )
                }>
                {userInfo?.role === 'ADMIN' && (
                  <Button type="primary" danger htmlType="button" loading={isDeleteLoading} disabled={isDeleteLoading}>
                    삭제
                  </Button>
                )}
              </Popconfirm>

              <Button
                type="default"
                htmlType="button"
                onClick={setInitialValues}
                disabled={userInfo?.role === 'SUB-CUSTOMER'}>
                초기화
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={isPatchLoading}
                disabled={isPatchLoading || userInfo?.role === 'SUB-CUSTOMER'}>
                수정
              </Button>
            </Space>
          </Row>
        </Form>
      </ContentsLayout>
    </>
  );
};

export default EquipmentDetail;
